import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import * as actions from 'redux/actions'
import * as filterHomepageActions from 'redux/modules/filterHomepage/actions'

import {
  MAX_ADOLESCENT_AGE,
  MAX_KID_AGE,
  MIN_KID_AGE,
  MIN_ADOLESCENT_AGE,
} from 'settings/_meetsAgesSettings'

import { Approach, FilterHomepageState, FilterState, Gender, Age } from '../MoreFiltersModal.types'

interface useMoreFiltersParams {
  handleCloseMoreFiltersModal: () => void
}

interface useMoreFiltersReturn {
  approaches: Approach[]
  clearNameTermError: () => void
  professionalNameTerm: string
  handleApplyFilters: () => void
  onChangeApproach: (approach: Approach) => void
  onChangeProfessionalNameTerm: (term: string) => void
  onChangeGender: (gender: Gender) => void
  onClearFilters: () => void
  selectedApproach: string
  selectedGender: Gender
  professionalNameTermIsToShortError: boolean
  ageRanges: string[]
  onChangeAgeRanges: (ageRanges: string[]) => void
  ages: Age[]
  onChangeAges: (ages: Age[]) => void
  ageRangeCategoryKidError: boolean
  ageRangeCategoryAdolescentError: boolean
  onChangeAgeRangeCategoryKidError: (value: boolean) => void
  onChangeAgeRangeCategoryAdolescentError: (value: boolean) => void
  selectedLanguages: string[]
  onChangeSelectedLanguages: (languages: string[]) => void
  shouldDisableClearFilterButton: boolean
}

const MIN_VALUE_TO_SEARCH_TEXT = 1

const initialAges: Age[] = [
  {
    id: 1,
    label: 'Criança',
    isSelected: false,
    category: 'KID',
  },
  {
    id: 2,
    label: 'Adolescente',
    isSelected: false,
    category: 'ADOLESCENT',
  },
  {
    id: 3,
    label: 'Adulto (18 a 59 anos)',
    isSelected: false,
    category: 'ADULT',
  },
  {
    id: 4,
    label: 'Idoso',
    isSelected: false,
    category: 'ELDERLY',
  },
]

export const useMoreFilters = ({ handleCloseMoreFiltersModal }: useMoreFiltersParams): useMoreFiltersReturn => {
  const dispatch = useDispatch()
  
  const currentSelectedGender = useSelector((state: FilterState) => state.filters.gender_status)
  const currentSelectedApproach = useSelector((state: FilterHomepageState) => state.filtersHomepage.actualApproach)
  const currentProfessionalNameTerm = useSelector((state: FilterHomepageState) => state.filtersHomepage.professionalNameTerm)
  const currentAgeRanges = useSelector((state: FilterHomepageState) => state.filtersHomepage.ageRanges)
  const currentLanguages = useSelector((state: FilterHomepageState) => state.filtersHomepage.languages)
  const approaches = useSelector((state: FilterHomepageState) => state.filtersHomepage.approaches)

  const [selectedGender, setSelectedGender] = useState<Gender>(currentSelectedGender || 'ALL')
  const [selectedApproach, setSelectedApproach] = useState(currentSelectedApproach || null)
  const [professionalNameTerm, setProfessionalNameTerm] = useState(currentProfessionalNameTerm || '')
  const [ages, setAges] = useState<Age[]>(initialAges)
  const [ageRanges, setAgeRanges] = useState(currentAgeRanges)

  const [selectedLanguages, setSelectedLanguages] = useState(currentLanguages)

  const [professionalNameTermIsToShortError, setProfessionalNameTermIsToShortError] = useState(false)
  const [ageRangeCategoryKidError, setAgeRangeCategoryKidError] = useState(false)
  const [ageRangeCategoryAdolescentError, setAgeRangeCategoryAdolescentError] = useState(false)

  const shouldDisableClearFilterButton = 
    selectedGender === 'ALL' && 
    !selectedApproach && 
    professionalNameTerm === '' &&
    ageRanges.length === 0 &&
    selectedLanguages.length === 0

  useEffect(() => {
    if (professionalNameTerm.length > MIN_VALUE_TO_SEARCH_TEXT) {
      setProfessionalNameTermIsToShortError(false)
    }
  }, [professionalNameTerm])
  
  function applyFilters() {
    dispatch(actions.genderStatus(selectedGender))
    dispatch(filterHomepageActions.setActualPropertyValue('actualApproach', selectedApproach))
    dispatch(filterHomepageActions.setActualPropertyValue('professionalNameTerm', professionalNameTerm))
    dispatch(filterHomepageActions.setActualPropertyValue('ageRanges', ageRanges))
    dispatch(filterHomepageActions.setActualPropertyValue('languages', selectedLanguages))

    handleCloseMoreFiltersModal()
  }

  function isInvalidAgeRange() {
    const selectedAges = ages.filter(age => age.isSelected)
    const selectedAgesIsKid = selectedAges.some(age => age.category === 'KID')
    const selectedAgesIsAdolescent = selectedAges.some(age => age.category === 'ADOLESCENT')
    
    const startAgeRangesValues = ageRanges.map(age => Number(age?.substring(0, age?.indexOf('-'))))
    const hasSelectedAgeRangeInKid = startAgeRangesValues.some(startAge => startAge >= MIN_KID_AGE && startAge <= MAX_KID_AGE)
    const hasSelectedAgeRangeInAdolescent = startAgeRangesValues.some(startAge => startAge >= MIN_ADOLESCENT_AGE && startAge <= MAX_ADOLESCENT_AGE)

    const hasErrorOnKidAgeRangeSelection = selectedAgesIsKid && !hasSelectedAgeRangeInKid
    const hasErrorOnAdolescentAgeRangeSelection = selectedAgesIsAdolescent && !hasSelectedAgeRangeInAdolescent

    setAgeRangeCategoryKidError(hasErrorOnKidAgeRangeSelection)
    setAgeRangeCategoryAdolescentError(hasErrorOnAdolescentAgeRangeSelection)

    return hasErrorOnKidAgeRangeSelection || hasErrorOnAdolescentAgeRangeSelection
  }
  
  function handleApplyFilters() {
    if (professionalNameTerm.length === MIN_VALUE_TO_SEARCH_TEXT) {
      return setProfessionalNameTermIsToShortError(true)
    }

    if (isInvalidAgeRange()) {
      return null
    }
  
    if (shouldDisableClearFilterButton) {
      return handleCloseMoreFiltersModal()
    }

    return applyFilters()
  }

  function onClearLocalFilters() {
    setSelectedGender('ALL')
    setSelectedApproach(null)
    setProfessionalNameTerm('')
    setAgeRanges([])
    setSelectedLanguages([])
  }

  function onClearReduxFilters() {
    dispatch(actions.genderStatus('ALL'))
    dispatch(filterHomepageActions.setActualPropertyValue('actualApproach', null))
    dispatch(filterHomepageActions.setActualPropertyValue('professionalNameTerm', ''))
    dispatch(filterHomepageActions.setActualPropertyValue('ageRanges', []))
    dispatch(filterHomepageActions.setActualPropertyValue('languages', []))
  }

  function onClearFilters() {
    onClearLocalFilters()
    onClearReduxFilters()
  }

  function onChangeGender(code: Gender) {
    setSelectedGender(code)
  }

  function onChangeApproach(approach: Approach) {
    setSelectedApproach(approach.code)
  }

  function onChangeProfessionalNameTerm(term: string) {
    setProfessionalNameTerm(term)
  }

  function onChangeAges(ages: Age[]) {
    setAges(ages)
  }

  function onChangeAgeRangeCategoryKidError(value: boolean) {
    setAgeRangeCategoryKidError(value)
  }

  function onChangeAgeRangeCategoryAdolescentError(value: boolean) {
    setAgeRangeCategoryAdolescentError(value)
  }

  const onChangeAgeRanges = useCallback((ageRanges: string[]) => {
    setAgeRanges(ageRanges)
  }, [])

  const onChangeSelectedLanguages = useCallback((languages: string[]) => {
    setSelectedLanguages(languages)
  }, [])

  function clearNameTermError() {
    setProfessionalNameTermIsToShortError(false)
  }

  return {
    approaches,
    clearNameTermError,
    selectedApproach,
    professionalNameTerm,
    handleApplyFilters,
    onClearFilters,
    onChangeApproach,
    onChangeProfessionalNameTerm,
    onChangeGender,
    selectedGender,
    professionalNameTermIsToShortError,
    ageRanges,
    onChangeAgeRanges,
    ages,
    onChangeAges,
    ageRangeCategoryAdolescentError,
    ageRangeCategoryKidError,
    onChangeAgeRangeCategoryKidError,
    onChangeAgeRangeCategoryAdolescentError,
    selectedLanguages,
    onChangeSelectedLanguages,
    shouldDisableClearFilterButton
  }
}